// @ts-nocheck
import React from "react";
import clsx from "clsx";
import ReactECharts from "echarts-for-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { listSites } from "../../api/sites";
import { getSiteOverview, getTransformerOverview, reprocessAnalytics } from "../../api/analytics";
import { TransformerCard } from "./cards";

const normalizeId = (val: any) => (val === null || typeof val === "undefined" ? "" : String(val));
const buildTransformerKey = (tr: any, idx = 0) => {
  const raw = tr?.array_id ?? tr?.id ?? tr?.transformer_id;
  if (raw !== null && typeof raw !== "undefined") return normalizeId(raw);
  const name = tr?.name || tr?.transformer_name || tr?.array_name;
  if (name) return `name-${name}`;
  return `idx-${idx}`;
};

const periods = [
  { key: "day", label: "1 Day" },
  { key: "week", label: "1 Week" },
  { key: "month", label: "1 Month" },
];

const views = [
  { key: "cards", label: "Cards view" },
  { key: "list", label: "List view" },
];

const PeriodSelector = ({ value, onChange }) => (
  <div className="flex items-center gap-2 rounded-full bg-slate-100 px-1 py-1 text-sm">
    {periods.map((p) => (
      <button
        key={p.key}
        onClick={() => onChange(p.key)}
        className={`rounded-full px-3 py-1 transition ${
          value === p.key ? "bg-white text-blue-700 shadow-sm" : "text-slate-600 hover:text-slate-800"
        }`}
      >
        {p.label}
      </button>
    ))}
  </div>
);

const SkeletonCard = () => (
  <div className="h-44 rounded-2xl border border-slate-100 bg-slate-100/80 animate-pulse" />
);

const ErrorBadge = ({ message }) => (
  <div className="flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
    <span className="text-lg">⚠️</span>
    <div>{message}</div>
  </div>
);

const formatEnergyValue = (value?: number | null) => {
  if (value === null || typeof value === "undefined") return "—";
  if (value >= 1000) return `${(value / 1000).toFixed(1)} MWh`;
  if (value >= 1) return `${value.toFixed(1)} kWh`;
  return `${(value * 1000).toFixed(0)} Wh`;
};

const toNumber = (val: any): number | null => {
  if (val === null || typeof val === "undefined") return null;
  if (typeof val === "number" && Number.isFinite(val)) return val;
  const parsed = parseFloat(String(val));
  return Number.isFinite(parsed) ? parsed : null;
};

export default function SiteHierarchy() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const headerRef = React.useRef<HTMLDivElement | null>(null);
  const [sites, setSites] = React.useState([]);
  const [selectedSite, setSelectedSite] = React.useState(searchParams.get("siteId") || "");
  const [period, setPeriod] = React.useState(searchParams.get("period") || "day");
  const [viewMode, setViewMode] = React.useState(searchParams.get("view") || "cards");
  const [transformers, setTransformers] = React.useState([]);
  const [inverters, setInverters] = React.useState([]);
  const [inverterCache, setInverterCache] = React.useState<Record<string, any[]>>({});
  const inverterLoadKeyRef = React.useRef<string | null>(null);
  const overviewRequestIdRef = React.useRef(0);
  const inverterRequestIdRef = React.useRef(0);
  const [loading, setLoading] = React.useState(false);
  const [loadingInverters, setLoadingInverters] = React.useState(false);
  const [error, setError] = React.useState("");
  const [hasFreshData, setHasFreshData] = React.useState(false);
  const [runningAnalytics, setRunningAnalytics] = React.useState(false);
  const [showWarnModal, setShowWarnModal] = React.useState(false);
  const [headerHeight, setHeaderHeight] = React.useState(0);

  React.useEffect(() => {
    let ignore = false;
    const loadSites = async () => {
      try {
        const data = await listSites();
        if (ignore) return;
        setSites(data || []);
        if (!selectedSite && data && data.length > 0) {
          const first = String(data[0].id);
          setSelectedSite(first);
          setSearchParams({ siteId: first, period, view: viewMode });
        }
      } catch (err) {
        if (!ignore) {
          setError("Unable to load sites.");
        }
      }
    };
    loadSites();
    return () => {
      ignore = true;
    };
  }, []);

  React.useLayoutEffect(() => {
    const compute = () => {
      const h = headerRef.current?.getBoundingClientRect()?.height || 0;
      setHeaderHeight(h);
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);


  const loadOverview = React.useCallback(
    async (siteId: string, currentPeriod: string) => {
      if (!siteId) return;
      const requestId = ++overviewRequestIdRef.current;
      setLoading(true);
      setError("");
      try {
        const overview = await getSiteOverview(siteId, currentPeriod);
        if (requestId !== overviewRequestIdRef.current) return;
        const rows = overview?.transformers || overview || [];
        rows.sort((a: any, b: any) => {
          const an = (a.name || a.array_name || "").toString().toLowerCase();
          const bn = (b.name || b.array_name || "").toString().toLowerCase();
          return an.localeCompare(bn);
        });
        setTransformers(rows);
        (window as any).__hierarchyTransformers = rows;
      } catch (err) {
        if (requestId !== overviewRequestIdRef.current) return;
        setTransformers([]);
        setError("Unable to load hierarchy overview.");
      } finally {
        if (requestId === overviewRequestIdRef.current) {
          setLoading(false);
        }
      }
    },
    []
  );

  React.useEffect(() => {
    if (selectedSite) {
      setInverterCache({});
      inverterLoadKeyRef.current = null;
      inverterRequestIdRef.current += 1;
      setInverters([]);
      setTransformers([]);
      loadOverview(selectedSite, period);
    }
  }, [selectedSite, period, loadOverview]);

  const handlePeriodChange = (key: string) => {
    setPeriod(key);
    setSearchParams({ siteId: selectedSite, period: key, view: viewMode });
  };

  const handleSiteChange = (e) => {
    const value = e.target.value;
    setSelectedSite(value);
    setSearchParams({ siteId: value, period, view: viewMode });
  };

  const handleShowInverters = (row) => {
    const transformerId = row.transformer_id ?? row.id ?? row.array_id;
    navigate(`/dashboard/hierarchy/transformer/${transformerId}`, {
      state: {
        siteId: selectedSite,
        period,
        transformerName: row.name || row.array_name || row.transformer_name,
        arrayId: row.array_id,
      },
      replace: false,
    });
  };

  const handleRunAnalytics = async () => {
    if (!selectedSite) return;
    setRunningAnalytics(true);
    setError("");
    try {
      await reprocessAnalytics(selectedSite, period);
      setHasFreshData(true);
    } catch (err) {
      setError("Analytics run failed. Please try again.");
    } finally {
    setRunningAnalytics(false);
    }
  };

  const handleRefresh = () => {
    if (!selectedSite) return;
    loadOverview(selectedSite, period);
    setHasFreshData(false);
  };

  const handleViewModeChange = (key: string) => {
    setViewMode(key);
    setSearchParams({ siteId: selectedSite, period, view: key });
  };

  const loadInverterDetails = React.useCallback(
    async (currentTransformers, currentPeriod, force: boolean = false) => {
      if (!selectedSite || !currentTransformers || currentTransformers.length === 0) return;
      const loadKey = `${selectedSite}-${currentPeriod}`;
      if (!force) {
        if (inverterLoadKeyRef.current === loadKey && inverters.length > 0) {
          return;
        }
        const cachedRows = inverterCache[loadKey];
        if (cachedRows && cachedRows.length > 0) {
          setInverters(cachedRows);
          inverterLoadKeyRef.current = loadKey;
          (window as any).__hierarchyInverters = cachedRows;
          return;
        }
      }
      const requestId = ++inverterRequestIdRef.current;
      setLoadingInverters(true);
      try {
        const payloads = await Promise.all(
          currentTransformers.map(async (tr, idx) => {
            const transformerKey = buildTransformerKey(tr, idx);
            // API expects transformer_id in the path; array_id is passed as query
            const transformerIdRaw = tr.transformer_id ?? tr.id ?? tr.array_id ?? transformerKey;
            const arrayIdParam = tr.array_id ?? tr.id ?? undefined;
            const transformerName = tr.name || tr.array_name || tr.transformer_name;
            const resp = await getTransformerOverview(transformerIdRaw, currentPeriod, arrayIdParam);
            const invs = resp?.inverters || resp || [];
            return { transformerId: transformerKey, transformerName, inverters: invs, transformerIndex: idx };
          })
        );
        if (requestId !== inverterRequestIdRef.current) return;
        const mapSeries = (arr: any[], fallbackValue?: number) => {
          if (!Array.isArray(arr)) return [];
          const mapped = arr
            .map((p: any, idx: number) => {
              const tsRaw = p.timestamp ?? p.t ?? p.ts ?? p.time ?? p[0];
              const val = p.z ?? p[1] ?? null;
              if (val === null || typeof val !== "number") return null;
              const ts =
                tsRaw !== undefined && tsRaw !== null
                  ? new Date(tsRaw).toISOString()
                  : new Date(Date.now() - (arr.length - idx) * 60000).toISOString();
              return { x: ts, y: val };
            })
            .filter(Boolean);
          if ((!mapped || mapped.length === 0) && typeof fallbackValue === "number") {
            mapped.push({ x: new Date().toISOString(), y: fallbackValue });
          }
          return mapped;
        };
        const rows = payloads.flatMap((payload) =>
          payload.inverters.map((inv, invIdx) => {
            const transformerKey = normalizeId(payload.transformerId) || normalizeId(payload.transformerName) || `tr-${payload.transformerIndex}`;
            const inverterKey =
              normalizeId(inv.inverter_id ?? inv.id) ||
              normalizeId(inv.name || inv.inverter_name) ||
              `inv-${invIdx}`;
            const rowKey = `${transformerKey}-${inverterKey}-${invIdx}`;
            return {
              rowKey,
              transformerId: transformerKey,
              transformerName: payload.transformerName,
              inverterId: inverterKey,
              inverterName: inv.name || inv.inverter_name || `INV-${inv.inverter_id || inv.id || invIdx}`,
              energyReal: toNumber(inv.energy_real),
              energyExpected: toNumber(inv.energy_expected),
              pr: toNumber(inv.pr_median),
              availability: toNumber(inv.availability),
              severityDay:
                inv.severity_day !== undefined
                  ? Number(inv.severity_day) || 0
                  : inv.z_severity === "critical"
                  ? 2
                  : inv.z_severity === "warning"
                  ? 1
                  : 0,
              startupStatus: inv.startup_status ?? 0,
              zLive:
                currentPeriod === "day"
                  ? toNumber(inv.z_median ?? inv.z_live_median ?? inv.z_latest)
                  : toNumber(inv.z_live_median ?? inv.z_median ?? inv.z_latest),
              zTodaySeries: mapSeries(inv.z_today_series || inv.z_recent || [], inv.z_live_median ?? inv.z_median),
              zSeries:
                currentPeriod === "day"
                  ? mapSeries(inv.z_today_series || [], inv.z_live_median ?? inv.z_median)
                  : mapSeries(inv.z_recent || [], inv.z_live_median ?? inv.z_median),
              hasValidPrToday: inv.has_valid_pr_today ?? true,
              diagnosis: inv.diagnosis_json,
            };
          })
        );
        setInverters(rows);
        inverterLoadKeyRef.current = loadKey;
        (window as any).__hierarchyInverters = rows;
        setInverterCache((prev) => ({ ...prev, [`${selectedSite}-${currentPeriod}`]: rows }));
      } catch (err) {
        if (requestId !== inverterRequestIdRef.current) return;
        setError("Unable to load inverter details.");
        setInverters([]);
      } finally {
        if (requestId === inverterRequestIdRef.current) {
          setLoadingInverters(false);
        }
      }
    },
    [selectedSite, inverters.length, inverterCache]
  );

  React.useEffect(() => {
    if ((viewMode === "list" || showWarnModal) && transformers.length > 0) {
      loadInverterDetails(transformers, period, false);
    }
  }, [viewMode, showWarnModal, transformers, period, loadInverterDetails]);

  const siteSummary = React.useMemo(() => {
    const totalReal = transformers.reduce((acc, r) => acc + (r.energy_real || 0), 0);
    const totalExpected = transformers.reduce((acc, r) => acc + (r.energy_expected || 0), 0);
    const prValue =
      totalExpected > 0 ? totalReal / totalExpected : transformers.length ? transformers.reduce((acc, r) => acc + (r.pr_median || 0), 0) / transformers.length : null;
    const availValues = transformers.map((r) => r.availability).filter((v) => typeof v === "number");
    const availability = availValues.length ? availValues.reduce((a, b) => a + b, 0) / availValues.length : null;

    // Day view severity counters
    const hasDayInverters = period === "day" && inverters.length > 0;
    const warnCount =
      hasDayInverters
        ? inverters.filter((inv) => inv.severityDay === 1).length
        : (transformers.reduce((acc, r) => acc + (r.warn_inverter_count ?? r.warn_count ?? 0), 0) as number);
    const critCount =
      hasDayInverters
        ? inverters.filter((inv) => inv.severityDay === 2).length
        : (transformers.reduce((acc, r) => acc + (r.crit_inverter_count ?? r.crit_count ?? 0), 0) as number);

    return {
      prValue,
      availability,
      warnCount,
      critCount,
    };
  }, [transformers, inverters, period]);

  const issueRows = React.useMemo(() => inverters.filter((inv) => inv.severityDay && inv.severityDay > 0), [inverters]);

  const dateLabel = React.useMemo(() => {
    const today = new Date();
    if (period === "week") return "Last 7 days";
    if (period === "month") return "Last 30 days";
    return today.toLocaleDateString();
  }, [period]);

  return (
    <div className="space-y-4">
      <div
        ref={headerRef}
        data-hierarchy-header
        className="sticky top-0 z-20 flex flex-col gap-3 rounded-xl border border-slate-200 bg-white/90 px-4 py-3 shadow-sm backdrop-blur"
      >
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-slate-900">Hierarchy Overview</h1>
            <select
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none"
              value={selectedSite}
              onChange={handleSiteChange}
            >
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <PeriodSelector value={period} onChange={handlePeriodChange} />
            <div className="flex items-center gap-2 rounded-full bg-slate-100 px-1 py-1 text-sm">
              {views.map((v) => (
                <button
                  key={v.key}
                  onClick={() => handleViewModeChange(v.key)}
                  className={`rounded-full px-3 py-1 transition ${
                    viewMode === v.key ? "bg-white text-blue-700 shadow-sm" : "text-slate-600 hover:text-slate-800"
                  }`}
                >
                  {v.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={handleRunAnalytics}
              disabled={!selectedSite || runningAnalytics}
              className={`rounded-xl border px-3 py-2 text-sm font-semibold transition ${
                runningAnalytics
                  ? "border-slate-200 bg-slate-100 text-slate-400"
                  : "border-blue-200 bg-white text-blue-700 hover:border-blue-400 hover:text-blue-900"
              }`}
            >
              {runningAnalytics ? "Running..." : "Run analytics"}
            </button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-700">
          {period !== "day" && (
            <>
              <div className="flex items-center gap-2">
                <span className="font-semibold">PR ({period})</span>
                <span className="text-slate-900">
                  {siteSummary.prValue !== null && typeof siteSummary.prValue !== "undefined"
                    ? siteSummary.prValue.toFixed(2)
                    : "—"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-semibold">Availability ({period})</span>
                <span className="text-slate-900">
                  {siteSummary.availability !== null && typeof siteSummary.availability !== "undefined"
                    ? `${siteSummary.availability.toFixed(1)}%`
                    : "—"}
                </span>
              </div>
            </>
          )}
          <div className="ml-auto flex items-center gap-3">
            <div className="flex items-center gap-1 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
              WARN: {siteSummary.warnCount}
            </div>
            <div className="flex items-center gap-1 rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700">
              CRIT: {siteSummary.critCount}
            </div>
            <button
              type="button"
              onClick={() => setShowWarnModal(true)}
              className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-blue-700 hover:border-blue-400"
            >
              SHOW
            </button>
          </div>
        </div>
      </div>

      {hasFreshData && (
        <div className="flex items-center justify-between rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900 shadow-sm">
          <div className="flex items-center gap-2">
            <span className="text-lg">•</span>
            <span>New data has been computed</span>
          </div>
          <button
            type="button"
            onClick={handleRefresh}
            className="rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-sm font-semibold text-blue-700 hover:border-blue-400"
          >
            Refresh
          </button>
        </div>
      )}

      {error && <ErrorBadge message={error} />}

      {viewMode === "list" ? (
        <InverterListView
          period={period}
          rows={inverters}
          loading={loadingInverters || loading}
          dateLabel={dateLabel}
          headerHeight={headerHeight}
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
          {loading &&
            Array.from({ length: 6 }).map((_, idx) => (
              <SkeletonCard key={idx} />
            ))}
          {!loading && transformers.length === 0 && (
            <div className="col-span-full rounded-xl border border-slate-200 bg-white p-6 text-center text-slate-500">
              No transformers found for this site and period.
            </div>
          )}
          {!loading &&
            transformers.map((tr, idx) => {
              const trId = buildTransformerKey(tr, idx);
              const matchingInverters = inverters.filter((inv) => inv.transformerId === trId);
              const dayWarn = matchingInverters.filter((inv) => inv.severityDay === 1).length;
              const dayCrit = matchingInverters.filter((inv) => inv.severityDay === 2).length;
              const daySeverity = matchingInverters.length
                ? matchingInverters.reduce((acc, inv) => Math.max(acc, inv.severityDay || 0), 0)
                : (tr.severity_day ?? 0);
              return (
                <TransformerCard
                  key={trId}
                  name={tr.name || tr.transformer_name || `TR-${trId}`}
                  energyReal={tr.energy_real}
                  energyExpected={tr.energy_expected}
                  prMedian={tr.pr_median}
                  prLabel={
                    period === "week"
                      ? "PR_cluster_week"
                      : period === "month"
                      ? "PR_cluster_month"
                      : "PR_cluster_day"
                  }
                  availability={tr.availability}
                  warnCount={
                    period === "day"
                      ? matchingInverters.length
                        ? dayWarn
                        : tr.warn_inverter_count ?? tr.warn_count ?? 0
                      : tr.warn_inverter_count ?? tr.warn_count
                  }
                  critCount={
                    period === "day"
                      ? matchingInverters.length
                        ? dayCrit
                        : tr.crit_inverter_count ?? tr.crit_count ?? 0
                      : tr.crit_inverter_count ?? tr.crit_count
                  }
                  severityDay={period === "day" ? daySeverity : tr.severity_day ?? 0}
                  onShowInverters={() => handleShowInverters(tr)}
                />
              );
            })}
        </div>
      )}

      {showWarnModal && (
        <WarnModal
          onClose={() => setShowWarnModal(false)}
          rows={issueRows}
          periodLabel={dateLabel}
          loading={loadingInverters}
        />
      )}
    </div>
  );
}

const StatusBadge = ({ severity }: { severity: number }) => {
  const tone =
    severity === 2
      ? "bg-rose-50 text-rose-700 border-rose-100"
      : severity === 1
      ? "bg-amber-50 text-amber-700 border-amber-100"
      : "bg-emerald-50 text-emerald-700 border-emerald-100";
  const label = severity === 2 ? "CRIT" : severity === 1 ? "WARN" : "NORMAL";
  return <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${tone}`}>{label}</span>;
};

const InverterListView = ({ rows, loading, period, dateLabel, headerHeight = 0 }) => {
  const filterRef = React.useRef<HTMLDivElement | null>(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [containerHeight, setContainerHeight] = React.useState<number>(480);

  const [sortKey, setSortKey] = React.useState("transformer");
  const [sortDir, setSortDir] = React.useState<"asc" | "desc">("asc");
  const [severityFilter, setSeverityFilter] = React.useState<number[]>([]);
  const [transformerFilter, setTransformerFilter] = React.useState<string[]>([]);
  const [trendModal, setTrendModal] = React.useState<{ name: string; series: { day: string; z: number }[]; zLabel: string } | null>(null);

  React.useLayoutEffect(() => {
    const compute = () => {
      const filterRect = filterRef.current?.getBoundingClientRect();
      const containerRect = containerRef.current?.getBoundingClientRect();
      const top = filterRect?.bottom || containerRect?.top || 0;
      const available = window.innerHeight - top - 24;
      setContainerHeight(available > 300 ? available : 300);
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [rows, headerHeight]);

  const transformerOptions = React.useMemo(() => {
    const map = new Map<string, string>();
    (rows || []).forEach((r: any) => {
      const id = r.transformerId ?? r.transformerName ?? "";
      const label = r.transformerName || r.transformerId || "";
      if (!id) return;
      const key = String(id);
      if (!map.has(key)) {
        map.set(key, String(label || id));
      }
    });
    return Array.from(map.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  const normalizedTransformerFilter = React.useMemo(() => transformerFilter.map((v) => String(v)), [transformerFilter]);

  const filteredRows = React.useMemo(() => {
    let data = [...(rows || [])];
    if (normalizedTransformerFilter.length > 0) {
      const tf = new Set(normalizedTransformerFilter);
      data = data.filter((r) => {
        const key = r.transformerId ?? r.transformerName;
        const keyVal = key === null || typeof key === "undefined" ? "" : String(key);
        return keyVal ? tf.has(keyVal) : false;
      });
    }
    if (severityFilter.length > 0) {
      const sevSet = new Set(severityFilter.map((v) => Number(v)));
      data = data.filter((r) => sevSet.has(Number(r.severityDay || 0)));
    }
    return data;
  }, [rows, normalizedTransformerFilter, severityFilter]);

  const sortedRows = React.useMemo(() => {
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
    const numericKeys = new Set(["pr", "energy", "expected", "availability", "severity", "startup", "z"]);

    const getSortValue = (row: any) => {
      switch (sortKey) {
        case "pr":
          return row.pr;
        case "energy":
          return row.energyReal;
        case "expected":
          return row.energyExpected;
        case "availability":
          return row.availability;
        case "severity":
          return row.severityDay;
        case "startup":
          return row.startupStatus;
        case "z":
          return row.zLive;
        case "inverter":
          return row.inverterName;
        case "transformer":
        default:
          return row.transformerName;
      }
    };

    const normalizeNumeric = (val: any) => {
      if (val === null || typeof val === "undefined") return null;
      if (typeof val === "number") return val;
      const parsed = parseFloat(String(val));
      return Number.isNaN(parsed) ? null : parsed;
    };

    const compareValues = (aVal: any, bVal: any, dir: number) => {
      if (numericKeys.has(sortKey)) {
        const aNum = normalizeNumeric(aVal);
        const bNum = normalizeNumeric(bVal);
        if (aNum === null && bNum === null) return 0;
        if (aNum === null) return 1;
        if (bNum === null) return -1;
        if (aNum === bNum) return 0;
        return aNum > bNum ? dir : -dir;
      }

      const aStr = aVal === null || typeof aVal === "undefined" ? "" : String(aVal);
      const bStr = bVal === null || typeof bVal === "undefined" ? "" : String(bVal);
      if (!aStr && !bStr) return 0;
      if (!aStr) return 1;
      if (!bStr) return -1;
      return collator.compare(aStr, bStr) * dir;
    };

    const dir = sortDir === "asc" ? 1 : -1;
    return [...filteredRows].sort((a, b) => {
      const primary = compareValues(getSortValue(a), getSortValue(b), dir);
      if (primary !== 0) return primary;
      // Tie-breaker for stable-ish ordering
      const aKey = `${a.transformerName || ""} ${a.inverterName || ""}`;
      const bKey = `${b.transformerName || ""} ${b.inverterName || ""}`;
      return collator.compare(aKey, bKey);
    });
  }, [filteredRows, sortKey, sortDir]);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  return (
    <>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-hierarchy-list>
        <div
          ref={filterRef}
          className="flex flex-wrap items-center gap-3 border-b border-slate-100 bg-white px-4 py-3 text-sm"
        >
          <div className="font-semibold text-slate-800">Inverters — {dateLabel}</div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-slate-700">Transformer</span>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setTransformerFilter([])}
                  className={clsx(
                    "rounded-full border px-3 py-1 text-[11px] font-semibold",
                    transformerFilter.length === 0 ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-white text-slate-600 border-slate-200"
                  )}
                >
                  All
                </button>
                {transformerOptions.map((opt) => {
                  const id = String(opt.id);
                  const active = normalizedTransformerFilter.includes(id);
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() =>
                        setTransformerFilter((prev) =>
                          active ? prev.filter((v) => String(v) !== id) : [...prev, id]
                        )
                      }
                      className={clsx(
                        "rounded-full border px-3 py-1 text-[11px] font-semibold",
                        active ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"
                      )}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-slate-700">Severity</span>
              <div className="flex flex-wrap gap-2">
                {[
                  { key: 0, label: "Normal", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
                  { key: 1, label: "Warning", className: "bg-amber-50 text-amber-700 border-amber-200" },
                  { key: 2, label: "Critical", className: "bg-rose-50 text-rose-700 border-rose-200" },
                ].map((opt: any) => {
                  const active = severityFilter.includes(opt.key);
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() =>
                        setSeverityFilter((prev) =>
                          active ? prev.filter((v) => v !== opt.key) : [...prev, opt.key]
                        )
                      }
                      className={clsx(
                        "rounded-full border px-3 py-1 text-[11px] font-semibold",
                        active ? opt.className : "bg-white text-slate-600 border-slate-200"
                      )}
                    >
                      {opt.label}
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => setSeverityFilter([])}
                  className={clsx(
                    "rounded-full border px-3 py-1 text-[11px] font-semibold",
                    severityFilter.length === 0 ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-white text-slate-600 border-slate-200"
                  )}
                >
                  All
                </button>
              </div>
            </div>
          </div>
        </div>
        <div
          ref={containerRef}
          className="mt-2 rounded-3xl border border-slate-100 bg-white shadow-sm"
          style={{ height: containerHeight }}
        >
          <div className="h-full overflow-auto">
            <table className="min-w-full divide-y divide-slate-100 text-sm">
              <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                    {[
                      { key: "transformer", label: "Transformer" },
                      { key: "inverter", label: "Inverter" },
                      { key: "energy", label: "Energy" },
                      { key: "expected", label: "Expected" },
                      { key: "pr", label: "PR" },
                      { key: "availability", label: "Availability" },
                      { key: "severity", label: "Severity" },
                      { key: "startup", label: "Startup" },
                      { key: "z", label: "Performance Deviation" },
                      { key: "chart", label: "" },
                    ].map((col) => (
                      <th
                        key={col.key}
                        className="cursor-pointer px-4 py-3"
                        onClick={() => handleSort(col.key)}
                      >
                      <div className="flex items-center gap-1">
                        <span>{col.label}</span>
                        {sortKey === col.key && <span>{sortDir === "asc" ? "▲" : "▼"}</span>}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && (
                  <tr>
                    <td colSpan={8} className="px-4 py-4 text-center text-slate-500">
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && sortedRows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-4 text-center text-slate-500">
                      No inverters found.
                    </td>
                  </tr>
                )}
                {!loading &&
                  sortedRows.map((row) => {
                    const diagTooltip =
                      row.hasValidPrToday === false
                        ? "No valid PR data available today."
                        : row.diagnosis
                        ? `${row.diagnosis.title || "Diagnosis"}: ${row.diagnosis.message || ""}${
                            Array.isArray(row.diagnosis.suggested_checks) && row.diagnosis.suggested_checks.length
                              ? ` | Checks: ${row.diagnosis.suggested_checks.join("; ")}`
                              : ""
                          }`
                        : "";
                    return (
                      <tr key={row.rowKey || `${row.transformerId}-${row.inverterId}`} className="hover:bg-slate-50">
                        <td className="px-4 py-2 text-slate-800">{row.transformerName}</td>
                      <td className="px-4 py-2 text-slate-800">{row.inverterName}</td>
                      <td className="px-4 py-2 text-slate-700">{formatEnergyValue(row.energyReal)}</td>
                      <td className="px-4 py-2 text-slate-700">{formatEnergyValue(row.energyExpected)}</td>
                      <td className="px-4 py-2 text-slate-700">
                        {row.hasValidPrToday === false
                            ? "No data"
                            : row.pr !== null && typeof row.pr !== "undefined"
                            ? row.pr.toFixed(2)
                            : "—"}
                        </td>
                        <td className="px-4 py-2 text-slate-700">
                          {row.availability !== null && typeof row.availability !== "undefined"
                            ? `${row.availability.toFixed(1)}%`
                            : "—"}
                        </td>
                        <td className="px-4 py-2">
                          <span title={diagTooltip} className="inline-flex items-center gap-1">
                            <StatusBadge severity={row.hasValidPrToday === false ? 0 : row.severityDay || 0} />
                          </span>
                        </td>
                      <td className="px-4 py-2 text-slate-800">
                        {row.startupStatus === 3 ? "⛔" : row.startupStatus === 2 ? "⚠️" : ""}
                      </td>
                      <td className="px-4 py-2 text-slate-800">
                        {row.zLive !== null && typeof row.zLive !== "undefined" ? row.zLive.toFixed(2) : "—"}
                        </td>
                        <td className="px-4 py-2 text-slate-800">
                          {row.zSeries && row.zSeries.length > 0 ? (
                            <button
                              type="button"
                              className="rounded-full border border-slate-200 bg-white px-2 py-1 text-xs text-blue-700 hover:border-blue-400"
                              onClick={() =>
                                setTrendModal({
                                  name: row.inverterName,
                                  series: row.zSeries.map((p: any) => ({ day: p.x, z: p.y })),
                                  zLabel: period === "day" ? "Today" : period === "week" ? "Last 7 days" : "Last 30 days",
                                })
                              }
                              title="View performance deviation trend"
                            >
                              📈
                            </button>
                          ) : (
                            ""
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {trendModal && <TrendModal data={trendModal} onClose={() => setTrendModal(null)} />}
    </>
  );
};

const WarnModal = ({ rows, onClose, periodLabel, loading }) => {
  return (
    <div className="fixed inset-0 z-30 flex items-start justify-center bg-slate-900/40 px-4 py-10 backdrop-blur">
      <div className="relative w-full max-w-4xl rounded-2xl border border-slate-200 bg-white shadow-xl">
        <div className="sticky top-0 flex items-center justify-between border-b border-slate-100 bg-white px-4 py-3">
          <div>
            <div className="text-lg font-semibold text-slate-900">Inverters with warnings</div>
            <div className="text-xs text-slate-500">{periodLabel}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-200 bg-white px-3 py-1 text-sm font-semibold text-slate-700 hover:border-slate-400"
          >
            Close
          </button>
        </div>
        <div className="max-h-[70vh] overflow-auto">
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="sticky top-0 z-10 bg-white text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Transformer</th>
                <th className="px-4 py-3">Inverter</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Raised</th>
                <th className="px-4 py-3">Z value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr>
                  <td colSpan={5} className="px-4 py-4 text-center text-slate-500">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-4 text-center text-slate-500">
                    No warnings or critical alerts.
                  </td>
                </tr>
              )}
              {!loading &&
                rows.map((row) => (
                  <tr key={`${row.transformerId}-${row.inverterId}`} className="hover:bg-slate-50">
                    <td className="px-4 py-2 text-slate-800">{row.transformerName}</td>
                    <td className="px-4 py-2 text-slate-800">{row.inverterName}</td>
                    <td className="px-4 py-2">
                      <span title={row.diagnosis?.message || ""} className="inline-flex items-center gap-1">
                        <StatusBadge severity={row.severityDay || 0} />
                      </span>
                    </td>
                    <td className="px-4 py-2 text-slate-700">{periodLabel}</td>
                    <td className="px-4 py-2 text-slate-800">
                      {row.zLive !== null && typeof row.zLive !== "undefined" ? row.zLive.toFixed(2) : "—"}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const TrendModal = ({ data, onClose }: { data: { name: string; series: { day: string; z: number }[]; zLabel: string }; onClose: () => void }) => {
  const series = Array.isArray(data.series) ? data.series : [];
  const hasData = series.length > 0;
  const zValues = series.map((p) => p.z);
  const options = {
    color: ["#0ea5e9"],
    grid: { top: 24, right: 24, bottom: 72, left: 48 },
    dataZoom: [
      {
        type: "inside",
        xAxisIndex: 0,
        filterMode: "none",
        zoomOnMouseWheel: true,
        moveOnMouseMove: true,
        moveOnMouseWheel: false,
      },
      {
        type: "slider",
        xAxisIndex: 0,
        filterMode: "none",
        height: 16,
        bottom: 24,
      },
    ],
    tooltip: {
      trigger: "axis",
      valueFormatter: (v: number) => Number(v).toFixed(2),
    },
    xAxis: {
      type: "time",
      axisLabel: { color: "#475569" },
      axisLine: { lineStyle: { color: "#cbd5e1" } },
      splitLine: { show: false },
    },
    yAxis: {
      type: "value",
      min: hasData ? Math.min(-3, Math.min(...zValues) - 0.5) : -3,
      max: hasData ? Math.max(3, Math.max(...zValues) + 0.5) : 3,
      axisLabel: { color: "#475569", formatter: (v: number) => v.toFixed(1) },
      splitLine: { lineStyle: { color: "#e2e8f0" } },
    },
    series: [
      {
        name: "Z",
        type: "line",
        smooth: true,
        symbol: "circle",
        symbolSize: 5,
        lineStyle: { width: 2 },
        data: series.map((p) => [p.day, p.z]),
        markLine: {
          symbol: "none",
          silent: true,
          lineStyle: { color: "#94a3b8", type: "dashed" },
          label: {
            formatter: "Zero",
            color: "#fff",
            backgroundColor: "#94a3b8",
            padding: [3, 6],
            borderRadius: 3,
          },
          data: [{ yAxis: 0 }],
        },
      },
    ],
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 px-4 py-10 backdrop-blur">
      <div className="w-full max-w-3xl rounded-2xl border border-slate-200 bg-white p-4 shadow-xl">
        <div className="mb-2 flex items-center justify-between">
          <div>
            <div className="text-lg font-semibold text-slate-900">{data.name}</div>
            <div className="text-xs text-slate-500">Z trend ({data.zLabel})</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-200 bg-white px-3 py-1 text-sm font-semibold text-slate-700 hover:border-slate-400"
          >
            Close
          </button>
        </div>
        {hasData ? (
          <ReactECharts option={options as any} style={{ height: 320, width: "100%" }} notMerge lazyUpdate />
        ) : (
          <div className="flex h-40 items-center justify-center text-sm text-slate-500">No deviation data available.</div>
        )}
      </div>
    </div>
  );
};

export { PeriodSelector };
